import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ChildrenOutletContexts, NavigationEnd, NavigationStart, PRIMARY_OUTLET, Router } from '@angular/router';
import { AppCookieService, AppLanguageService, SharedService } from '@ace/ace';
import { CookieService } from 'ngx-cookie-service';
import { TranslationService } from '@ace/ace';
import { filter } from 'rxjs';
import { environment } from '../environments/environments';
import { InstructorService } from './services/instructor.service';
import { PageRefreshService } from './core/services/page-refresh.service';
import { buildInstructorBrandingTranslationOverrides } from './core/branding/instructor-branding.config';
import { PlatformFeaturesService } from './services/platform-features.service';
import { AccessibilitySettingsService } from './services/accessibility-settings.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  menu;
  title = 'ABG';
  isLoading: boolean = false;
  isSettingsModalOpen = false;
  private unlistenProfileIconClick?: () => void;
  private unlistenSideMenuClick?: () => void;
  private hasSyncedInstructorProfilePicture = false;
  private readonly profileImageUpdatedEvent = 'ace-profile-image-updated';
  private readonly profileImageSyncDelays = [0, 500, 1500];
  private readonly profileSettingsLinkAttribute = 'data-instructor-settings-link';
  private profileImageSyncTimers: ReturnType<typeof setTimeout>[] = [];
  constructor(
    private renderer: Renderer2,
    private translationService: TranslationService,
    private sharedService: SharedService,
    private router: Router,
    private cookieService: AppCookieService,
    private appLanguageService: AppLanguageService,
    private instructorService: InstructorService,
    private outletContexts: ChildrenOutletContexts,
    private pageRefreshService: PageRefreshService,
    private platformFeaturesService: PlatformFeaturesService,
    private accessibilitySettingsService: AccessibilitySettingsService,
  )
  {}

  ngOnInit() {
    console.log('[DEBUG] AppComponent ngOnInit fired! Current URL:', window.location.href, 'Cookies:', document.cookie);
    this.accessibilitySettingsService.applyCurrentSettings();
    this.syncAceUserIdentity();

    // DEBUG: Cookie watchdog - checks every 500ms if cookie disappears
    let lastCookieState = !!this.cookieService.getCookie(`${environment.appVar}_accessToken`);
    setInterval(() => {
      const hasCookie = !!this.cookieService.getCookie(`${environment.appVar}_accessToken`);
      if (lastCookieState && !hasCookie) {
        console.error('[DEBUG WATCHDOG] accessToken cookie DISAPPEARED! URL:', window.location.href, 'Cookies:', document.cookie);
      }
      lastCookieState = hasCookie;
    }, 500);

    // DEBUG: Detect full page unload (window.location change)
    window.addEventListener('beforeunload', () => {
      console.error('[DEBUG] PAGE UNLOADING! URL: ' + window.location.href);
    });

    // DEBUG: Track ALL navigations
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe((e: NavigationStart) => {
        const token = this.cookieService.getCookie(`${environment.appVar}_accessToken`) || '';
        const msg = `[NAV] NavigationStart → ${e.url} | Token present: ${!!token} | Token length: ${token.length} | Trigger: ${e.navigationTrigger || 'unknown'}`;
        console.log(msg);
        if (e.url === '/login' || e.url.startsWith('/login')) {
          console.error('[DEBUG NAV] Navigating to LOGIN!', msg, 'Cookies:', document.cookie);
        }
      });

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(async (e: NavigationEnd) => {
        const url = e.urlAfterRedirects;
        if (url === '/login') {
          this.refreshPlatformFeatures();
          this.renderer.setAttribute(document.documentElement, 'dir', 'ltr');
        } else {
          const saved = localStorage.getItem('appLang') || 'en';
          this.renderer.setAttribute(
            document.documentElement,
            'dir',
            saved === 'ar' ? 'rtl' : 'ltr'
          );
          await this.initializeTranslations(saved);
          this.syncInstructorProfilePicture();
          setTimeout(() => this.ensureProfileSettingsMenuItem(), 0);
        }
      });
    const initialUrl = this.router.url;
    if (initialUrl === '/login') {
      this.refreshPlatformFeatures();
      this.renderer.setAttribute(document.documentElement, 'dir', 'ltr');
    } else {
      const saved = localStorage.getItem('appLang') || 'en';
      this.renderer.setAttribute(
        document.documentElement,
        'dir',
        saved === 'ar' ? 'rtl' : 'ltr'
      );
      void this.initializeTranslations(saved);
      this.syncInstructorProfilePicture();
      setTimeout(() => this.ensureProfileSettingsMenuItem(), 0);
    }

    this.loadTheme();
    this.sharedService.isLoading.subscribe(l => (this.isLoading = l));

    let lastLanguageCode: string | null = null;
    this.appLanguageService.selectedLanguage$.subscribe((language) => {
      const isoCode = language?.isoCode || null;
      if (!isoCode) return;
      if (lastLanguageCode === null) {
        lastLanguageCode = isoCode;
        return;
      }
      if (isoCode !== lastLanguageCode) {
        lastLanguageCode = isoCode;
        localStorage.setItem('appLang', isoCode);
        location.reload();
      }
    });

    this.bindProfileIconNavigation();
    this.bindSideMenuRefresh();
  }

  ngOnDestroy(): void {
    this.unlistenProfileIconClick?.();
    this.unlistenSideMenuClick?.();
    this.clearProfilePictureSyncTimers();
  }

  private bindProfileIconNavigation(): void {
    const handleProfileMenuClick = (event: MouseEvent) => {
      this.ensureProfileSettingsMenuItem();

      const target = this.getEventElement(event.target);
      const settingsLink = target?.closest(`[${this.profileSettingsLinkAttribute}]`);

      if (!settingsLink) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.openSettingsModal();
    };

    document.addEventListener('click', handleProfileMenuClick, true);
    this.unlistenProfileIconClick = () => {
      document.removeEventListener('click', handleProfileMenuClick, true);
    };
  }

  private ensureProfileSettingsMenuItem(): void {
    const dropdowns = Array.from(document.querySelectorAll('.user-menu-dropdown .profile-dropdown'));
    const label = this.translationService.translate('Settings') || 'Settings';

    dropdowns.forEach(dropdown => {
      const existing = dropdown.querySelector(`[${this.profileSettingsLinkAttribute}]`);
      if (existing) {
        const labelElement = existing.querySelector('span');
        if (labelElement) {
          labelElement.textContent = label;
        }
        return;
      }

      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'dropdown-item notify-item';
      item.setAttribute(this.profileSettingsLinkAttribute, 'true');

      const icon = document.createElement('i');
      icon.className = 'fa-solid fa-gear fs-16 align-middle';
      icon.setAttribute('aria-hidden', 'true');

      const text = document.createElement('span');
      text.textContent = label;

      item.appendChild(icon);
      item.appendChild(text);

      const profileItem = dropdown.querySelector('.fa-user')?.closest('.dropdown-item');
      const insertBefore = profileItem?.nextSibling ?? dropdown.firstChild;
      dropdown.insertBefore(item, insertBefore);
    });
  }

  private bindSideMenuRefresh(): void {
    const handleSideMenuClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = this.getEventElement(event.target);
      const sidebarContainer = target?.closest('.app-sidebar-menu, #sidebar-menu');

      if (!sidebarContainer) {
        return;
      }

      const currentUrl = `${window.location.pathname}${window.location.search}`;
      const sideMenuLink = target?.closest('a, button, [role="button"], [routerlink], [ng-reflect-router-link]');

      if (!sideMenuLink || !this.isSideMenuContentClick(event, target, sideMenuLink)) {
        return;
      }

      const href = sideMenuLink?.getAttribute('href')?.trim();
      const routerLink = (
        sideMenuLink?.getAttribute('routerlink') ||
        sideMenuLink?.getAttribute('ng-reflect-router-link')
      )?.trim();
      const destination = href || routerLink;

      if (destination?.startsWith('#') || destination?.startsWith('javascript:')) {
        return;
      }

      if (destination) {
        const resolvedUrl = new URL(destination, window.location.origin);
        const clickedUrl = `${resolvedUrl.pathname}${resolvedUrl.search}`;

        if (clickedUrl !== currentUrl) {
          return;
        }

        setTimeout(() => this.refreshActivePage(currentUrl), 0);
        return;
      }

      if (sideMenuLink.hasAttribute('aria-expanded')) {
        return;
      }

      setTimeout(() => {
        const settledUrl = `${window.location.pathname}${window.location.search}`;
        if (settledUrl === currentUrl) {
          this.refreshActivePage(currentUrl);
        }
      }, 0);
    };

    document.addEventListener('click', handleSideMenuClick, true);
    this.unlistenSideMenuClick = () => {
      document.removeEventListener('click', handleSideMenuClick, true);
    };
  }

  openSettingsModal(): void {
    this.isSettingsModalOpen = true;
  }

  closeSettingsModal(): void {
    this.isSettingsModalOpen = false;
  }

  private getEventElement(target: EventTarget | null): Element | null {
    if (target instanceof Element) {
      return target;
    }

    if (target instanceof Text && target.parentElement) {
      return target.parentElement;
    }

    return null;
  }

  private isSideMenuContentClick(event: MouseEvent, target: Element, sideMenuLink: Element): boolean {
    const menuArrow = target.closest('.menu-arrow');
    if (menuArrow && sideMenuLink.contains(menuArrow)) {
      return false;
    }

    if (target === sideMenuLink) {
      return true;
    }

    const contentTarget = target.closest(
      '.menu-fontawesome-icon, .menu-lucide-icon, .menu-fallback-icon, .menu-icon, .nav-icon, .menu-name, .menu-text, .nav-link-text, span, i, svg',
    );

    if (contentTarget && sideMenuLink.contains(contentTarget)) {
      return true;
    }

    return this.isPointInsideSideMenuContent(event, sideMenuLink);
  }

  private isPointInsideSideMenuContent(event: MouseEvent, sideMenuLink: Element): boolean {
    const contentElements = sideMenuLink.querySelectorAll(
      '.menu-fontawesome-icon, .menu-lucide-icon, .menu-fallback-icon, .menu-icon, .nav-icon, .menu-name, .menu-text, .nav-link-text, span, i, svg',
    );

    for (const element of Array.from(contentElements)) {
      if (element.closest('.menu-arrow')) {
        continue;
      }

      const rect = element.getBoundingClientRect();
      if (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      ) {
        return true;
      }
    }

    return false;
  }

  private refreshActivePage(expectedUrl: string): void {
    const activeUrl = `${window.location.pathname}${window.location.search}`;
    if (activeUrl !== expectedUrl) {
      return;
    }

    this.refreshPlatformFeatures();

    const component = this.getDeepestActivatedComponent();
    if (!component) {
      return;
    }

    if (typeof component.reloadPageData === 'function') {
      component.reloadPageData(true);
      return;
    }

    this.pageRefreshService.requestRefresh(activeUrl);

    if (typeof component.ngOnDestroy === 'function') {
      component.ngOnDestroy();
    }

    if (typeof component.ngOnInit === 'function') {
      component.ngOnInit();
    }
  }

  private getDeepestActivatedComponent(): any {
    let contexts: ChildrenOutletContexts | null = this.outletContexts;
    let activeComponent: any = null;

    while (contexts) {
      const context = contexts.getContext(PRIMARY_OUTLET);
      if (!context) {
        break;
      }

      if (context.outlet?.isActivated) {
        activeComponent = context.outlet.component;
      }

      contexts = context.children;
    }

    return activeComponent;
  }

  private refreshPlatformFeatures(): void {
    this.platformFeaturesService.refresh().subscribe();
  }

  private syncInstructorProfilePicture(): void {
    if (this.hasSyncedInstructorProfilePicture) {
      return;
    }

    const isLoginRoute = this.router.url === '/login' || this.router.url.startsWith('/login');
    if (isLoginRoute) {
      return;
    }

    this.hasSyncedInstructorProfilePicture = true;
    this.instructorService.getMe().subscribe({
      next: instructor => this.scheduleShellProfilePictureSync(instructor?.profilePictureUrl),
      error: () => {
        this.hasSyncedInstructorProfilePicture = false;
      }
    });
  }

  private scheduleShellProfilePictureSync(profilePictureUrl?: string | null): void {
    const imageUrl = (profilePictureUrl ?? '').trim();

    this.clearProfilePictureSyncTimers();
    this.updateShellProfilePicture(imageUrl);

    this.profileImageSyncTimers = this.profileImageSyncDelays.map(delay =>
      setTimeout(() => this.updateShellProfilePicture(imageUrl), delay)
    );
  }

  private clearProfilePictureSyncTimers(): void {
    this.profileImageSyncTimers.forEach(timer => clearTimeout(timer));
    this.profileImageSyncTimers = [];
  }

  private updateShellProfilePicture(imageUrl: string): void {
    if (imageUrl) {
      localStorage.setItem('profileImageUrl', imageUrl);
    } else {
      localStorage.removeItem('profileImageUrl');
    }

    window.dispatchEvent(new CustomEvent(this.profileImageUpdatedEvent, { detail: imageUrl }));
  }

  private syncAceUserIdentity(): void {
    const aceIdentity = this.cookieService.getCookie(`${environment.appVar}_identity`) || '';
    if (!aceIdentity) {
      return;
    }

    this.sharedService.userId = aceIdentity;
  }

  private async initializeTranslations(languageCode: string): Promise<void> {
    await this.translationService.init(languageCode);
    this.applyBrandingTranslationOverrides(languageCode);
  }

  private applyBrandingTranslationOverrides(languageCode: string): void {
    const overrides = buildInstructorBrandingTranslationOverrides(environment, languageCode);
    if (!Object.keys(overrides).length) {
      return;
    }

    const translationServiceState = this.translationService as unknown as {
      translations?: Map<string, string>;
    };

    const translations = translationServiceState.translations;
    if (!(translations instanceof Map)) {
      return;
    }

    for (const [key, value] of Object.entries(overrides)) {
      translations.set(key, value);
    }
  }


  loadTheme() {
    const isLoginRoute = this.router.url === '/login' || this.router.url.startsWith('/login');
    if (isLoginRoute) {
      // Keep login page visually stable regardless of previously selected app theme.
      document.documentElement.setAttribute('data-bs-theme', 'light');
      return;
    }

    let theme = localStorage.getItem("selectedTheme")
    if (theme == "theme1") {
      document.documentElement.setAttribute('data-bs-theme', 'light');
      /** Light Theme **/
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-2');
      const whiteColor = getComputedStyle(document.documentElement).getPropertyValue('--white-color');
      const lightGreyColor = getComputedStyle(document.documentElement).getPropertyValue('--light-grey-color');
      const darkRedColor = getComputedStyle(document.documentElement).getPropertyValue('--dark-red');
      const blackColor = getComputedStyle(document.documentElement).getPropertyValue('--black-color');
      /* Main */
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      /* Grid */
      document.documentElement.style.setProperty('--grid-icon-color', sourceColor);
      document.documentElement.style.setProperty('--grid-bg-color', whiteColor);
      document.documentElement.style.setProperty('--grid-text-color', lightGreyColor);
      document.documentElement.style.setProperty('--grid-del-icon', darkRedColor);
      document.documentElement.style.setProperty('--grid-view-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-edit-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-download-icon', sourceColor);
      document.documentElement.style.setProperty('--grid-column-name-color', blackColor);
      /* Sidebar */
      document.documentElement.style.setProperty('--sidebar-bg-color', whiteColor);
      document.documentElement.style.setProperty('--sidebar-icon-color', lightGreyColor);
      document.documentElement.style.setProperty('--sidebar-selected-icon-color', sourceColor);
      /* Box Color */
      document.documentElement.style.setProperty('--box-bg-color', whiteColor);
      document.documentElement.style.setProperty('--box-text-color', blackColor);
    } else if (theme == "theme2") {
      document.documentElement.setAttribute('data-bs-theme', 'dark');
      /** Dark Theme **/
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-2');
      const whiteColor = getComputedStyle(document.documentElement).getPropertyValue('--white-color');
      const lightGreyColor = getComputedStyle(document.documentElement).getPropertyValue('--light-grey-color');
      /* Main */
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      /* Grid */
      document.documentElement.style.setProperty('--grid-icon-color', whiteColor);
      document.documentElement.style.setProperty('--grid-bg-color', sourceColor);
      document.documentElement.style.setProperty('--grid-text-color', whiteColor);
      document.documentElement.style.setProperty('--grid-del-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-view-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-edit-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-download-icon', whiteColor);
      document.documentElement.style.setProperty('--grid-column-name-color', whiteColor);
      /* Sidebar */
      document.documentElement.style.setProperty('--sidebar-bg-color', sourceColor);
      document.documentElement.style.setProperty('--sidebar-icon-color', lightGreyColor);
      document.documentElement.style.setProperty('--sidebar-selected-icon-color', whiteColor);
      /* Box Color */
      document.documentElement.style.setProperty('--box-bg-color', sourceColor);
      document.documentElement.style.setProperty('--box-text-color', whiteColor);
    }
  }
}
